import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useConsole } from "../../hooks/useConsole";
import { ConsoleDevices } from "./ConsoleDevices";
import { ConsoleTable } from "./ConsoleTable";
import { ConsoleLog } from "./ConsoleLog";

// Setup -> Console: see both button boxes live, re-learn any control, save the mapping.
export const ConsolePanel = () => {
  const [settings, setSettings] = useState(null);
  const [map, setMap] = useState({});
  const [devices, setDevices] = useState({});
  const [learning, setLearning] = useState(null);
  const [log, setLog] = useState([]);
  const [pressed, setPressed] = useState({});
  const learn = useRef(null);
  learn.current = learning;

  useEffect(() => {
    api.get("/admin/settings").then((r) => { setSettings(r.data); setMap(r.data.console_map || {}); setDevices(r.data.console_devices || {}); });
    const onKey = (e) => { if (e.key === "Escape") setLearning(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onRaw = (raw) => {
    const l = learn.current;
    if (!l) return;
    if (l.type === "device" && raw.kind === "button") { setDevices((d) => ({ ...d, [l.role]: raw.gamepadId })); setLearning(null); toast.success(`${l.role === "controller" ? "PXN Controller" : "Macro Box"} = ${raw.gamepadId}`); return; }
    if (l.type !== "control") return;
    const keep = (m) => m[l.id] || {};
    if (l.kind === "axis" && raw.kind === "axis") { setMap((m) => ({ ...m, [l.id]: { ...keep(m), device: raw.device, kind: "axis", index: raw.index } })); setLearning(null); }
    else if (l.kind === "encoder" && raw.kind === "button") {
      if (l.step === "cw") setLearning({ ...l, step: "ccw", cw: raw.index, device: raw.device });
      else { setMap((m) => ({ ...m, [l.id]: { ...keep(m), device: l.device, kind: "encoder", cw: l.cw, ccw: raw.index } })); setLearning(null); }
    } else if (l.kind === "button" && raw.kind === "button") { setMap((m) => ({ ...m, [l.id]: { ...keep(m), device: raw.device, kind: "button", index: raw.index } })); setLearning(null); }
  };

  const onEvent = (ev) => {
    setLog((l) => [{ ...ev, at: Date.now() }, ...l].slice(0, 12));
    if (ev.type === "press") { setPressed((p) => ({ ...p, [ev.id]: Date.now() })); setTimeout(() => setPressed((p) => ({ ...p, [ev.id]: 0 })), 350); }
  };

  const con = useConsole({ map, devices, enabled: Boolean(settings), onEvent, onRaw });

  const save = async () => {
    try {
      await api.put("/admin/settings", { ...settings, console_map: map, console_devices: devices });
      toast.success("Console mapping saved");
    } catch (err) {
      toast.error(errMsg(err, "Could not save the mapping"));
    }
  };

  if (!settings) return null;
  return (
    <div data-testid="console-panel" className="grid gap-6">
      <ConsoleDevices connected={con.connected} devices={devices} learning={learning} onLearn={(role) => setLearning((l) => (l?.type === "device" && l.role === role ? null : { type: "device", role }))} />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,7fr)_minmax(0,4fr)]">
        <ConsoleTable map={con.map} overrides={map} dials={con.dials} toggles={con.toggles} pressed={pressed} learning={learning}
          onLearn={(id, kind) => setLearning((l) => (l?.type === "control" && l.id === id ? null : { type: "control", id, kind, step: kind === "encoder" ? "cw" : undefined }))}
          onResetOne={(id) => setMap((m) => { const n = { ...m }; delete n[id]; return n; })}
          onToggleEnabled={(id, on) => setMap((m) => ({ ...m, [id]: { ...(m[id] || {}), enabled: on } }))} />
        <ConsoleLog log={log} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button data-testid="console-reset-map" type="button" onClick={() => { setMap({}); setDevices({}); setLearning(null); }} className="lab-btn-ghost">Reset to the printed layout</button>
        <button data-testid="console-save" type="button" onClick={save} className="lab-btn-primary">Save console mapping</button>
      </div>
    </div>
  );
};
